import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ETIQUETA_TIPO_ACCESO } from '@cmms/shared';
import { ErrorApi } from '../servicios/api.js';
import { useSesion } from '../servicios/sesion.js';
import { api } from '../servicios/api.js';
import { notificarErrorGlobal } from './notificaciones-globales.js';
import {
  guardarTemaPreferido,
  obtenerTemaPreferido,
  guardarBarraLateralOculta,
  obtenerBarraLateralOculta,
  obtenerVistaOrdenesPreferida,
  type Tema,
} from '../servicios/preferencias.js';
import { Marca } from './marca.js';
import { LectorRfid } from './lector-rfid.js';
import {
  IconoActividad,
  IconoActivo,
  IconoAlerta,
  IconoCampana,
  IconoCatalogo,
  IconoDashboard,
  IconoFalla,
  IconoLicencia,
  IconoMedidor,
  IconoMenu,
  IconoOT,
  IconoQr,
  IconoReporte,
  IconoSolicitud,
  IconoUbicacion,
  IconoUsuarios,
  IconoProcedimiento,
  IconoParte,
  IconoLuna,
  IconoSol,
  IconoPlegar,
} from './iconos.js';
import type { Permiso } from '@cmms/shared';

interface Entrada {
  ruta: string;
  nombre: string;
  icono: JSX.Element;
  /** Un array es "alguno de estos": ej. Escanear QR le sirve tanto a quien
   *  gestiona activos como a un Solicitante reportando, sin permisos en común. */
  permiso: Permiso | Permiso[];
  contador?: number;
}

/** `entrada.permiso` puede ser uno o "alguno de varios" — ver `Entrada`. */
function tienePermisoEntrada(puede: (p: Permiso) => boolean, permiso: Permiso | Permiso[]): boolean {
  return Array.isArray(permiso) ? permiso.some(puede) : puede(permiso);
}

/**
 * Estructura de navegación del lienzo de diseño: TRABAJO, GESTIONAR y OPTIMIZAR.
 * Cada entrada declara el permiso que la habilita, de modo que un solicitante ve
 * exactamente las dos opciones que puede usar en lugar de un menú lleno de
 * puertas cerradas.
 */
function grupos(pendientes: number, alertasActivos: number): { titulo: string; entradas: Entrada[] }[] {
  return [
    {
      titulo: 'TRABAJO',
      entradas: [
        { ruta: '/dashboard', nombre: 'Dashboard', icono: <IconoDashboard />, permiso: 'solicitud:leer' },
        { ruta: '/ordenes', nombre: 'Órdenes de Trabajo', icono: <IconoOT />, permiso: 'ot:leer' },
        {
          ruta: '/solicitudes',
          nombre: 'Solicitudes',
          icono: <IconoSolicitud />,
          permiso: 'solicitud:leer',
          contador: pendientes,
        },
        { ruta: '/mis-tablets', nombre: 'Mis tablets', icono: <IconoQr />, permiso: 'solicitud:crear' },
        {
          ruta: '/qr',
          nombre: 'Escanear QR',
          icono: <IconoQr />,
          // Le sirve tanto a quien gestiona activos como a un Solicitante
          // reportando desde el celular — los dos casos de uso reales de
          // escanear, sin un permiso en común entre ambos roles.
          permiso: ['activo:leer', 'solicitud:crear'],
        },
        { ruta: '/notificaciones', nombre: 'Notificaciones', icono: <IconoCampana />, permiso: 'solicitud:leer' },
      ],
    },
    {
      titulo: 'GESTIONAR',
      entradas: [
        { ruta: '/activos', nombre: 'Activos', icono: <IconoActivo />, permiso: 'activo:leer' },
        { ruta: '/activos/inventarios', nombre: 'Inventarios', icono: <IconoCatalogo />, permiso: 'activo:leer' },
        {
          ruta: '/activos/alertas',
          nombre: 'Observaciones',
          icono: <IconoAlerta />,
          permiso: 'activo:leer',
          contador: alertasActivos,
        },
        { ruta: '/ubicaciones', nombre: 'Ubicaciones', icono: <IconoUbicacion />, permiso: 'ubicacion:leer' },
        { ruta: '/procedimientos', nombre: 'Procedimientos', icono: <IconoProcedimiento />, permiso: 'procedimiento:leer' },
        { ruta: '/partes', nombre: 'Partes', icono: <IconoParte />, permiso: 'parte:leer' },
        { ruta: '/medidores', nombre: 'Medidores', icono: <IconoMedidor />, permiso: 'medidor:leer' },
        { ruta: '/personal', nombre: 'Personal', icono: <IconoUsuarios />, permiso: 'sujeto_evaluable:leer' },
        { ruta: '/tipos-falla', nombre: 'Tipos de Fallas', icono: <IconoFalla />, permiso: 'tipo_falla:leer' },
        { ruta: '/usuarios', nombre: 'Grupos / Usuarios', icono: <IconoUsuarios />, permiso: 'usuario:leer' },
        { ruta: '/dispositivos', nombre: 'Dispositivos', icono: <IconoQr />, permiso: 'usuario:administrar' },
        { ruta: '/catalogos', nombre: 'Catálogos', icono: <IconoCatalogo />, permiso: 'catalogo:administrar' },
        { ruta: '/listas-materiales', nombre: 'Listas de materiales', icono: <IconoCatalogo />, permiso: 'lista_materiales:editar' },
      ],
    },
    {
      titulo: 'OPTIMIZAR',
      entradas: [
        { ruta: '/reportes', nombre: 'Reportes', icono: <IconoReporte />, permiso: 'reporte:leer' },
        { ruta: '/actividad', nombre: 'Actividad Reciente', icono: <IconoActividad />, permiso: 'auditoria:leer' },
        { ruta: '/licencia', nombre: 'Licencia', icono: <IconoLicencia />, permiso: 'licencia:leer' },
      ],
    },
    {
      titulo: 'GESTIÓN',
      entradas: [
        {
          ruta: '/gestion',
          nombre: 'Versiones de la app',
          icono: <IconoAlerta />,
          permiso: 'app_version:gestionar',
        },
      ],
    },
  ];
}

const TITULOS: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/ordenes': 'Órdenes de Trabajo',
  '/solicitudes': 'Solicitudes',
  '/mis-tablets': 'Mis tablets',
  '/notificaciones': 'Notificaciones',
  '/activos': 'Activos',
  '/ubicaciones': 'Ubicaciones',
  '/procedimientos': 'Procedimientos',
  '/ejecuciones': 'Ejecución de checklist',
  '/tipos-falla': 'Tipos de Fallas',
  '/usuarios': 'Grupos y Usuarios',
  '/dispositivos': 'Dispositivos',
  '/catalogos': 'Catálogos',
  '/listas-materiales': 'Listas de materiales',
  '/qr': 'Escanear QR',
  '/medidores': 'Medidores',
  '/personal': 'Personal',
  '/reportes': 'Reportes',
  '/actividad': 'Actividad Reciente',
  '/licencia': 'Licencia',
  '/gestion': 'Gestión',
};

/**
 * Scroll de `.contenido` por ruta, recordado mientras dure la sesión: sin
 * esto, volver de un detalle a una lista larga (de OT, activos, etc.)
 * dejaba al usuario arriba del todo en vez de donde había quedado
 * (§ pedido). Vive fuera del componente para sobrevivir a que se desmonte.
 */
const posicionesScroll = new Map<string, number>();

export function Disposicion(): JSX.Element {
  const { usuario, puede, salir, nucleoHabilitado, organizaciones, cargandoOrganizaciones, cargarOrganizaciones, cambiarOrganizacion } =
    useSesion();
  const ubicacion = useLocation();
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [cambiandoOrganizacion, setCambiandoOrganizacion] = useState(false);
  const contenidoRef = useRef<HTMLElement>(null);

  // Cambiar de página cierra el drawer: quedarse abierto tras navegar es lo
  // que hace que el usuario crea que el toque no funcionó (§16.8).
  useEffect(() => {
    setMenuAbierto(false);
  }, [ubicacion.pathname]);

  // Restaura el scroll de la ruta a la que se entra (una lista en la que ya
  // se había bajado) y, mientras se está en ella, registra la posición
  // actual para la próxima vez. Dos intentos: uno inmediato y otro tras el
  // primer paint, porque el contenido de la ruta nueva (con react-query
  // cache tibia, sin loading visible) puede terminar de tener la altura
  // real recién después del primer render.
  useEffect(() => {
    const el = contenidoRef.current;
    if (!el) return;
    const guardada = posicionesScroll.get(ubicacion.pathname) ?? 0;
    el.scrollTop = guardada;
    const id = requestAnimationFrame(() => {
      el.scrollTop = guardada;
    });
    return () => cancelAnimationFrame(id);
  }, [ubicacion.pathname]);

  useEffect(() => {
    const el = contenidoRef.current;
    if (!el) return;
    function guardarPosicion(): void {
      posicionesScroll.set(ubicacion.pathname, el!.scrollTop);
    }
    el.addEventListener('scroll', guardarPosicion, { passive: true });
    return () => el.removeEventListener('scroll', guardarPosicion);
  }, [ubicacion.pathname]);

  const puedeCambiarEmpresa = nucleoHabilitado && usuario?.modoAcceso === 'nucleo';

  const manejarCambioOrganizacion = async (orgId: string, detalles: HTMLDetailsElement | null) => {
    setCambiandoOrganizacion(true);
    try {
      await cambiarOrganizacion(orgId);
      if (detalles) detalles.open = false;
    } catch (error) {
      notificarErrorGlobal(error instanceof ErrorApi ? error.message : 'No se pudo cambiar de empresa.');
    } finally {
      setCambiandoOrganizacion(false);
    }
  };

  const { data: bandeja } = useQuery({
    queryKey: ['bandeja-resumen'],
    queryFn: () => api.get<{ estado: string; total: number }[]>('/solicitudes/bandeja'),
    enabled: puede('solicitud:leer_todas'),
    refetchInterval: 60_000,
  });

  const { data: observacionesActivoPendientes } = useQuery({
    queryKey: ['observaciones-activo-pendientes'],
    queryFn: () => api.get<unknown[]>('/activos/observaciones/pendientes'),
    enabled: puede('activo:leer'),
    refetchInterval: 60_000,
  });

  const { data: observacionesUbicacionPendientes } = useQuery({
    queryKey: ['observaciones-ubicacion-pendientes'],
    queryFn: () => api.get<unknown[]>('/ubicaciones/observaciones/pendientes'),
    enabled: puede('ubicacion:leer'),
    refetchInterval: 60_000,
  });

  const { data: observacionesSujetoPendientes } = useQuery({
    queryKey: ['observaciones-sujeto-pendientes'],
    queryFn: () => api.get<unknown[]>('/sujetos-evaluables/observaciones/pendientes'),
    enabled: puede('sujeto_evaluable:leer'),
    refetchInterval: 60_000,
  });

  const pendientes = bandeja?.find((b) => b.estado === 'PENDIENTE')?.total ?? 0;
  const alertasActivos =
    (observacionesActivoPendientes?.length ?? 0) +
    (observacionesUbicacionPendientes?.length ?? 0) +
    (observacionesSujetoPendientes?.length ?? 0);
  const raiz = `/${ubicacion.pathname.split('/')[1] ?? ''}`;
  const gruposMenu = grupos(pendientes, alertasActivos);

  // Rutas como «/activos» y «/activos/alertas» conviven en el menú: sin esto,
  // NavLink marca ambas activas a la vez al estar una dentro de la otra. Se
  // resuelve a mano, quedándose con la ruta más específica que calce.
  // Barra inferior de móvil: hasta 4 accesos de uso más frecuente, en vez del
  // menú completo (14 entradas no entran en una fila de iconos). El resto
  // del menú sigue disponible detrás de «Más», que abre el mismo drawer.
  // «Escanear QR» reemplaza a «Solicitudes» para quien gestiona activos: en
  // campo, el técnico escanea la etiqueta mucho más seguido que lo que entra
  // a revisar la bandeja. Pero para un Solicitante (sin `ot:leer`/`activo:leer`)
  // esa prioridad no aplica — «Solicitudes» va como quinto candidato, así que
  // a alguien con muy pocos permisos no le queda una barra con un solo ícono
  // útil (§ pedido).
  const CANDIDATOS_BARRA_INFERIOR = ['/dashboard', '/ordenes', '/qr', '/activos', '/solicitudes'];
  const entradasInferior = gruposMenu
    .flatMap((g) => g.entradas)
    .filter((e) => CANDIDATOS_BARRA_INFERIOR.includes(e.ruta))
    .filter((e) => tienePermisoEntrada(puede, e.permiso))
    .sort((a, b) => CANDIDATOS_BARRA_INFERIOR.indexOf(a.ruta) - CANDIDATOS_BARRA_INFERIOR.indexOf(b.ruta))
    .slice(0, 4);

  const todasLasRutas = gruposMenu.flatMap((g) => g.entradas.map((e) => e.ruta));
  const rutaActiva = todasLasRutas
    .filter((r) => ubicacion.pathname === r || ubicacion.pathname.startsWith(`${r}/`))
    .sort((a, b) => b.length - a.length)[0];
  // El enlace de «Órdenes de Trabajo» del menú es fijo (`/ordenes`), pero el
  // módulo tiene varias vistas con ruta propia (lista, calendario, Gantt) —
  // sin esto, salir a otro módulo desde una de ellas y volver por el menú
  // siempre caía en la lista, perdiendo la vista en la que el usuario estaba
  // (§ bug reportado).
  const vistaPreferida = obtenerVistaOrdenesPreferida();
  const destinoOrdenes =
    vistaPreferida === 'calendario' ? '/ordenes/calendario' : vistaPreferida === 'gantt' ? '/ordenes/gantt' : '/ordenes';
  const destinoDe = (ruta: string): string => (ruta === '/ordenes' ? destinoOrdenes : ruta);

  const [tema, setTema] = useState<Tema>(obtenerTemaPreferido);
  // Escritorio: esconder la barra lateral para ganar ancho (se recuerda).
  const [barraOculta, setBarraOculta] = useState<boolean>(obtenerBarraLateralOculta);
  const alternarBarra = (): void => {
    const siguiente = !barraOculta;
    guardarBarraLateralOculta(siguiente);
    setBarraOculta(siguiente);
  };

  const alternarTema = (): void => {
    const siguiente: Tema = tema === 'oscuro' ? 'claro' : 'oscuro';
    guardarTemaPreferido(siguiente);
    setTema(siguiente);
  };

  const iniciales = (usuario?.nombreCompleto ?? '')
    .split(' ')
    .slice(0, 2)
    .map((p) => p[0] ?? '')
    .join('')
    .toUpperCase();

  return (
    <div className={`app${barraOculta ? ' barra-oculta' : ''}`}>
      <div
        className={`capa-menu${menuAbierto ? ' abierta' : ''}`}
        role="presentation"
        onClick={() => setMenuAbierto(false)}
      />

      <nav
        className={`barra-lateral${menuAbierto ? ' abierta' : ''}`}
        aria-label="Navegación principal"
      >
        <div className="barra-lateral-cabecera">
          <div className="marca">
            <Marca />
          </div>
          <button
            type="button"
            className="barra-lateral-plegar"
            aria-label={barraOculta ? 'Mostrar menú lateral' : 'Ocultar menú lateral'}
            aria-expanded={!barraOculta}
            title={barraOculta ? 'Mostrar menú' : 'Ocultar menú'}
            onClick={alternarBarra}
          >
            <IconoPlegar />
          </button>
        </div>

        {gruposMenu.map((grupo) => {
          const visibles = grupo.entradas.filter((e) => tienePermisoEntrada(puede, e.permiso));
          if (visibles.length === 0) return null;

          return (
            <div className="nav-grupo" key={grupo.titulo}>
              <div className="nav-grupo-titulo">{grupo.titulo}</div>
              {visibles.map((entrada) => (
                <NavLink
                  key={entrada.ruta}
                  to={destinoDe(entrada.ruta)}
                  className={`nav-enlace${entrada.ruta === rutaActiva ? ' activo' : ''}`}
                >
                  {entrada.icono}
                  <span className="nav-enlace-texto">{entrada.nombre}</span>
                  {entrada.contador ? <span className="nav-contador">{entrada.contador}</span> : null}
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>

      <div className="principal">
        <header className="barra-superior">
          <button
            type="button"
            className="boton-menu"
            aria-label="Abrir menú de navegación"
            aria-expanded={menuAbierto}
            onClick={() => setMenuAbierto((v) => !v)}
          >
            <IconoMenu />
          </button>

          <div className="migas">
            {puedeCambiarEmpresa ? (
              <details
                className="selector-empresa"
                onToggle={(evento) => {
                  if (evento.currentTarget.open && organizaciones === null) void cargarOrganizaciones();
                }}
              >
                <summary>{usuario?.razonSocial}</summary>
                <div className="selector-empresa-menu">
                  {cargandoOrganizaciones && <div className="selector-empresa-vacio">Cargando empresas…</div>}
                  {!cargandoOrganizaciones && organizaciones?.length === 0 && (
                    <div className="selector-empresa-vacio">No perteneces a otras empresas.</div>
                  )}
                  {!cargandoOrganizaciones &&
                    organizaciones?.map((org) => (
                      <button
                        key={org.id}
                        type="button"
                        className={`selector-empresa-opcion${org.esActual ? ' activa' : ''}`}
                        disabled={org.esActual || cambiandoOrganizacion}
                        onClick={(evento) =>
                          void manejarCambioOrganizacion(org.id, evento.currentTarget.closest('details'))
                        }
                      >
                        {org.nombre}
                      </button>
                    ))}
                </div>
              </details>
            ) : (
              usuario?.razonSocial
            )}{' '}
            · <strong>{TITULOS[raiz] ?? 'Inicio'}</strong>
          </div>

          <div className="usuario-chip">
            <div className="avatar" aria-hidden="true">
              {iniciales || '?'}
            </div>
            <div className="usuario-datos">
              <div className="usuario-nombre">{usuario?.nombreCompleto}</div>
              <div className="usuario-rol">
                {usuario ? ETIQUETA_TIPO_ACCESO[usuario.tipoAcceso] : ''}
              </div>
            </div>
            <button
              type="button"
              className="boton-icono boton-tema"
              aria-label={tema === 'oscuro' ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro'}
              title={tema === 'oscuro' ? 'Tema claro' : 'Tema oscuro'}
              onClick={alternarTema}
            >
              {tema === 'oscuro' ? <IconoSol /> : <IconoLuna />}
            </button>
            <button type="button" className="boton secundario pequeno" onClick={() => void salir()}>
              Salir
            </button>
          </div>
        </header>

        <main className="contenido" ref={contenidoRef}>
          <Outlet />
        </main>
        <LectorRfid />
      </div>

      <nav className="nav-inferior" aria-label="Navegación principal (móvil)">
        {entradasInferior.map((entrada) => (
          <NavLink
            key={entrada.ruta}
            to={destinoDe(entrada.ruta)}
            className={`nav-inferior-enlace${entrada.ruta === rutaActiva ? ' activo' : ''}`}
            onClick={(e) => {
              // Tocar el módulo en el que ya se está, con la lista scrolleada
              // hacia abajo, es un patrón conocido para "subir": sin esto, el
              // único camino era scrollear a mano o saltar a otro módulo y
              // volver, solo para caer arriba de nuevo (§ pedido).
              if (entrada.ruta === rutaActiva) {
                e.preventDefault();
                contenidoRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
          >
            {entrada.icono}
            <span>{entrada.nombre === 'Órdenes de Trabajo' ? 'OT' : entrada.nombre}</span>
            {entrada.contador ? <span className="nav-contador">{entrada.contador}</span> : null}
          </NavLink>
        ))}
        <button
          type="button"
          className={`nav-inferior-enlace${menuAbierto ? ' activo' : ''}`}
          aria-label="Abrir menú completo"
          aria-expanded={menuAbierto}
          onClick={() => setMenuAbierto((v) => !v)}
        >
          <IconoMenu />
          <span>Más</span>
        </button>
      </nav>
    </div>
  );
}
